'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Dog, ChevronRight } from 'lucide-react';
import { PRODUCTS, type DogSize } from '@/lib/mockData';

const DOG_SIZES: DogSize[] = ['XS', 'S', 'M', 'L', 'XL'];

const SIZE_COLORS: Record<DogSize, string> = {
  XS: 'bg-pink-100 text-pink-700 border-pink-200 hover:bg-pink-200',
  S: 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200',
  M: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200',
  L: 'bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200',
  XL: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
};

const SIZE_ACTIVE: Record<DogSize, string> = {
  XS: 'bg-pink-500 text-white border-pink-500',
  S: 'bg-yellow-500 text-white border-yellow-500',
  M: 'bg-orange-500 text-white border-orange-500',
  L: 'bg-teal-600 text-white border-teal-600',
  XL: 'bg-blue-600 text-white border-blue-600',
};

const POPULAR_SEARCHES = [
  'Royal Canin Medium',
  'Croquettes sans céréales',
  'Orijen Puppy',
  'Pedigree Adult',
  'Hill\'s Science Plan',
];

export default function HeroSearch() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [selectedSize, setSelectedSize] = useState<DogSize | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length >= 2) {
      const q = query.toLowerCase();
      const matches = PRODUCTS
        .filter((p) => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q))
        .map((p) => p.name)
        .slice(0, 5);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSearch(q?: string) {
    const searchQ = q || query;
    const params = new URLSearchParams();
    if (searchQ.trim()) params.set('q', searchQ.trim());
    if (selectedSize) params.set('size', selectedSize);
    router.push(`/search-results?${params.toString()}`);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSearch();
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 pt-16 pb-24">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-400/20 rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl pointer-events-none" />

      <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center max-w-3xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/30">
            <span className="w-2 h-2 bg-teal-300 rounded-full animate-pulse" />
            Prix mis à jour aujourd'hui
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
            Les meilleures croquettes
            <br />
            <span className="text-amber-100">au meilleur prix</span>
          </h1>
          <p className="text-orange-100 text-lg md:text-xl mb-10 max-w-xl mx-auto">
            Comparez les prix de vos croquettes préférées sur des dizaines de boutiques en ligne en quelques secondes.
          </p>

          {/* Search box */}
          <div className="relative max-w-2xl mx-auto">
            <div className="flex items-center bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-white focus-within:border-teal-400 transition-all duration-200">
              <Search size={20} className="ml-4 text-orange-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => query.length >= 2 && setShowSuggestions(true)}
                placeholder="Rechercher une marque, un produit…"
                className="flex-1 px-3 py-4 text-base text-gray-800 placeholder-gray-400 outline-none bg-transparent"
              />
              <button
                onClick={() => handleSearch()}
                className="bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-bold px-6 py-4 text-base transition-all duration-150 flex items-center gap-2 shrink-0"
              >
                Comparer
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Autocomplete suggestions */}
            {showSuggestions && (
              <div
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-orange-100 overflow-hidden z-50 animate-fade-in"
              >
                {suggestions.map((s) => (
                  <button
                    key={`suggestion-${s.replace(/\s+/g, '-')}`}
                    onClick={() => {
                      setQuery(s);
                      setShowSuggestions(false);
                      handleSearch(s);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <Search size={14} className="text-orange-300 shrink-0" />
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Popular searches */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-orange-100 text-sm">Populaire :</span>
            {POPULAR_SEARCHES.map((s) => (
              <button
                key={`popular-${s.replace(/\s+/g, '-')}`}
                onClick={() => handleSearch(s)}
                className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded-full border border-white/30 transition-colors duration-150 backdrop-blur-sm"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Dog size quick filters */}
          <div className="mt-10">
            <p className="text-orange-100 text-sm font-medium mb-3 flex items-center justify-center gap-2">
              <Dog size={16} />
              Filtrer par taille de chien
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {DOG_SIZES.map((size) => (
                <button
                  key={`size-chip-${size}`}
                  onClick={() => setSelectedSize(selectedSize === size ? null : size)}
                  className={`badge-size text-sm px-4 py-2 transition-all duration-150 active:scale-95 ${
                    selectedSize === size
                      ? SIZE_ACTIVE[size]
                      : 'bg-white/90 text-gray-700 border-white/50 hover:bg-white'
                  }`}
                >
                  <span className="font-bold">{size}</span>
                  <span className="hidden sm:inline ml-1 font-normal opacity-80">
                    {size === 'XS' ? '< 4 kg' : size === 'S' ? '4–10 kg' : size === 'M' ? '11–25 kg' : size === 'L' ? '26–44 kg' : '45 kg+'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
            { value: '10', label: 'Produits comparés', key: 'stat-products' },
            { value: '8', label: 'Marchands suivis', key: 'stat-retailers' },
            { value: '€0', label: 'Coût du service', key: 'stat-cost' },
            { value: '24h', label: 'Mise à jour des prix', key: 'stat-update' },
          ].map((stat) => (
            <div key={stat.key} className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/30">
              <div className="font-display text-2xl font-extrabold text-white tabular-nums">{stat.value}</div>
              <div className="text-orange-100 text-xs mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="hsl(33, 100%, 97%)" />
        </svg>
      </div>
    </section>
  );
}